import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { fetchApi } from "@/lib/api";

export type OmnisearchType =
  | "customer"
  | "location"
  | "building"
  | "artifact"
  | "app"
  | "rolodex_org"
  | "rolodex_contact"
  | "case";

export interface OmnisearchItem {
  type: OmnisearchType;
  id: string;
  title: string;
  subtitle: string | null;
  url: string;
  email: string | null;
  tenants: string[];
  score: number;
}

export interface OmnisearchGroup {
  type: OmnisearchType;
  items: OmnisearchItem[];
}

export interface OmnisearchResponse {
  query: string;
  groups: OmnisearchGroup[];
  took_ms: number;
}

export const OMNISEARCH_MIN_CHARS = 2;

// Type-prefix filter: "c:levine" → only customers. Stripped from the sent query.
const TYPE_PREFIXES: Record<string, OmnisearchType> = { c: "customer", l: "location", b: "building", a: "artifact" };

export function parseOmnisearchQuery(raw: string): { q: string; types: OmnisearchType[] | null; pagesOnly: boolean } {
  const m = /^([clbap]):\s*(.*)$/i.exec(raw.trim());
  if (!m) return { q: raw.trim(), types: null, pagesOnly: false };
  const key = m[1].toLowerCase();
  if (key === "p") return { q: m[2], types: [], pagesOnly: true };
  return { q: m[2], types: [TYPE_PREFIXES[key]], pagesOnly: false };
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

/** Entity search (customers / locations / buildings / artifacts / Kory Apps / Rolodex / support cases) behind the ⌘K
 *  palette. Debounced, superseded requests aborted, previous results held so
 *  the list doesn't flicker between keystrokes. */
export function useOmnisearch(rawQuery: string) {
  const parsed = parseOmnisearchQuery(rawQuery);
  const q = useDebounced(parsed.q, 120);
  const types = parsed.types;
  const enabled = !parsed.pagesOnly && q.length >= OMNISEARCH_MIN_CHARS;
  const query = useQuery<OmnisearchResponse>({
    queryKey: ["omnisearch", q, types],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ q });
      if (types && types.length) params.set("types", types.join(","));
      return fetchApi(`/search?${params.toString()}`, { signal });
    },
    enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
  // ``q``: the (debounced) words this query is for, so a caller can tell its error from an earlier one's.
  return { ...query, q, groups: enabled ? (query.data?.groups ?? []) : [], pagesOnly: parsed.pagesOnly };
}

/** The most visible results a sent turn carries, so Kory can answer "the second one" without searching again. */
export const SEARCH_RESULTS_MAX = 8;

/** The type-ahead results showing when a turn is sent, as the model-only `<search-results>` block
 *  (hidden from the bubble and history by kory_chat.py), in on-screen order so "the second one"
 *  means the second row the person sees; null when there are none. */
export function serializeSearchResultsBlock(groups: OmnisearchGroup[]): string | null {
  return serializeSearchHits(groups.flatMap((group) => group.items).slice(0, SEARCH_RESULTS_MAX));
}

/** `items` as the `<search-results>` block, in the order given (the order on screen).
 *  A page row is `{type: "page", id: url}` and sits in the same list, so "the first one" is the first row. */
export function serializeSearchHits(
  items: readonly { type: string; id: string; title: string; subtitle: string | null; url: string }[],
): string | null {
  const hits = items.map(({ type, id, title, subtitle, url }) => ({ type, id, title, subtitle, url }));
  if (!hits.length) return null;
  // Drop a literal closing marker from the data so the block ends only at its own marker.
  const data = JSON.stringify(hits).replaceAll("</search-results>", "");
  return `<search-results>\n${data}\n</search-results>`;
}
